import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideMapPin,
  lucideMapPinOff,
  lucidePencil,
  lucidePlus,
  lucideRotateCw,
  lucideStore,
  lucideTrash2,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { getCountryName } from '../../../../../config/countries.config';
import {
  StoreLocation,
  StoreLocationService,
} from '../../../../../services/store-location.service';
import { StoreLocationDialogComponent } from './components/store-location-dialog/store-location-dialog.component';

/**
 * Settings → Shop-Einstellungen → Weitere Standorte: shops besides the business
 * address. The storefront lists them together, the business address first.
 */
@Component({
  selector: 'app-store-locations',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmAlertDialogImports,
    HlmButtonImports,
    HlmCardImports,
    HlmEmptyImports,
    HlmItemImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTooltipImports,
    StoreLocationDialogComponent,
  ],
  providers: [
    provideIcons({
      lucideMapPin,
      lucideMapPinOff,
      lucidePencil,
      lucidePlus,
      lucideRotateCw,
      lucideStore,
      lucideTrash2,
      lucideTriangleAlert,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './store-locations.component.html',
})
export class StoreLocationsComponent {
  private readonly service = inject(StoreLocationService);

  protected readonly locations = signal<StoreLocation[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly actionError = signal<string | null>(null);

  protected readonly dialogOpen = signal(false);
  /** The location in the dialog; null while adding. */
  protected readonly editing = signal<StoreLocation | null>(null);
  protected readonly confirmDelete = signal<StoreLocation | null>(null);
  protected readonly deletingId = signal<number | null>(null);

  protected readonly getCountryName = getCountryName;

  constructor() {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.list().subscribe({
      next: (locations) => {
        this.locations.set(locations);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Deine Standorte konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  protected add(): void {
    this.editing.set(null);
    this.actionError.set(null);
    this.dialogOpen.set(true);
  }

  protected edit(location: StoreLocation): void {
    this.editing.set(location);
    this.actionError.set(null);
    this.dialogOpen.set(true);
  }

  protected onSaved(saved: StoreLocation): void {
    this.locations.update((list) =>
      list.some((l) => l.id === saved.id)
        ? list.map((l) => (l.id === saved.id ? saved : l))
        : [...list, saved],
    );
    this.dialogOpen.set(false);
  }

  protected onDeleteDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed') this.confirmDelete.set(null);
  }

  protected delete(): void {
    const location = this.confirmDelete();
    this.confirmDelete.set(null);
    if (!location || this.deletingId() !== null) return;
    this.deletingId.set(location.id);
    this.actionError.set(null);
    this.service.delete(location.id).subscribe({
      next: () => {
        this.locations.update((list) => list.filter((l) => l.id !== location.id));
        this.deletingId.set(null);
      },
      error: () => {
        this.deletingId.set(null);
        this.actionError.set(
          `„${location.name}“ konnte nicht gelöscht werden. Bitte versuche es erneut.`,
        );
      },
    });
  }
}
